import { useMemo } from "react"

import { useI18n } from "@/shared/i18n"
import {
  usePvpRoomPhase,
  usePvpRoomPhaseDurations,
  usePvpRoomTimers,
} from "../hooks/usePvpRoomSelectors"
import { useLiveRemainingMs } from "../hooks/useLiveRemainingMs"

interface BetSheetHeaderProps {
  roomId: string
  /** Defaults to `betting.sheetYourBet`. */
  title?: string
  sectorNowPercent: number
  sectorAfterPercent: number
}

const TIMER_DASH_LENGTH = 62.83 // 2 * PI * 10 (matches Bet sheets)

/**
 * Common top section of bet-related bottom sheets:
 * drag-handle slot, betting-phase countdown ring (left), title + sector
 * now/after labels (centered), close-button slot (right).
 *
 * Used by `BetSettingsSheet` (TON betting) and `GiftPickerSheet` (gift betting)
 * to keep the two sheets visually identical.
 */
export function BetSheetHeader({
  roomId,
  title,
  sectorNowPercent,
  sectorAfterPercent,
}: BetSheetHeaderProps) {
  const { t } = useI18n()
  const phase = usePvpRoomPhase(roomId)
  const timers = usePvpRoomTimers(roomId)
  const durations = usePvpRoomPhaseDurations(roomId)
  const liveBetting = useLiveRemainingMs(
    timers?.endsAtBettingMs ?? null,
    phase === "BETTING"
  )

  const bettingProgress = useMemo(() => {
    if (phase !== "BETTING" || liveBetting == null) return null
    const total = durations?.bettingDurationMs ?? null
    if (!total || total <= 0) return null
    return Math.min(1, Math.max(0, 1 - liveBetting / total))
  }, [durations?.bettingDurationMs, liveBetting, phase])

  const showTimer =
    phase === "BETTING" &&
    liveBetting != null &&
    liveBetting > 0 &&
    bettingProgress != null

  const dashOffset = TIMER_DASH_LENGTH * (1 - (bettingProgress ?? 0))
  const resolvedTitle = title ?? t("betting.sheetYourBet")

  return (
    <>
      <div className="flex h-4 items-center justify-center">
        <div className="h-1 w-8 rounded-[9999px] bg-white/10" />
      </div>

      <div className="flex h-14 items-center px-4 pr-1">
        <div className="flex h-14 w-14 items-center justify-center">
          {showTimer ? (
            <svg
              className="h-6 w-6 -rotate-90"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                fill="none"
                stroke="rgba(255,255,255,0.16)"
                strokeWidth="3"
              />
              <circle
                cx="12"
                cy="12"
                r="10"
                fill="none"
                stroke="#FFFF00"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray={TIMER_DASH_LENGTH}
                strokeDashoffset={dashOffset}
                style={{ transition: "stroke-dashoffset 0.25s linear" }}
              />
            </svg>
          ) : (
            <div className="h-6 w-6" />
          )}
        </div>
        <div className="flex flex-1 flex-col items-center gap-1">
          <p className="text-[24px] font-semibold leading-[29px] tracking-[-0.5px] text-white">
            {resolvedTitle}
          </p>
          <p className="text-xs leading-[15px] tracking-[-0.5px] text-white/60">
            {t("betting.sheetSectorNow", { percent: sectorNowPercent })}{" "}
            •{" "}
            <span className="text-green-100">
              {t("betting.sheetSectorAfter", { percent: sectorAfterPercent })}
            </span>
          </p>
        </div>
        {/* Spacer to balance the close button rendered by SheetContent. */}
        <div className="h-14 w-14 shrink-0" aria-hidden />
      </div>
    </>
  )
}
